import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import {
  useLazyViewSolarEstimationQuery,
  useUserSelectionMutation,
} from "../../../../redux/api/solar-estimation/solarEstimationAPI";
import { selectAuth } from "../../../../redux/features/auth/auth-slice";
import { ISolarEstimationUserSelection } from "../../../../shared/oversight-core/dtos/request-dtos/solar-estimation-user-selection-request-dto";
import {
  IViewSolarEstimationData,
  IViewSolarEstimationOutputParams,
  IViewSolarRecommendations,
} from "../../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-response-dto";
import { ESolarSolutionType } from "../../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSystemType } from "../../../../shared/oversight-core/enums/solar-system-type ";
import { EViewSolarEstimationSolarProductCategory } from "../../../../shared/oversight-core/enums/view-solar-estimation-solar-product-category";
import { Configurations } from "../../../../shared/oversight-core/interfaces/configurations";
import { IEstimatorDetails } from "../../../../shared/oversight-core/interfaces/estimator-details";
import { IViewSolarEstimationSolarProductAndOtherDetails } from "../../../../shared/oversight-core/interfaces/view-solar-estimation-solar-system";
import AppSwitch from "../../../../shared/oversight-core/ui-elements/app-switch/app-switch";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Search from "../../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import { filterByCategories } from "../../../../shared/oversight-core/utils/filter-by-categories";
import { formatWithCommas } from "../../../../shared/oversight-core/utils/format-with-commas";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import { sortRecommendations } from "../../../../shared/oversight-core/utils/solar-estimation";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";
import UpdateHistoryModal from "../../../solar-sim-dashboard/estimations/components/update-history-modal/update-history-modal";
import PreferencesModal from "../preferences-modal/preferences-modal";
import RecommendedSolarComponentOption from "../recommended-solar-component-option/recommended-solar-component-option";
import SolarComponentOption from "../solar-component-option/solar-component-option";
import SolarComponent, {
  ISelectedSolarComponent,
} from "../solar-component/solar-component";
import styles from "./assemble-system.module.scss";

interface IProps {
  assembleSystemData: IViewSolarEstimationData | undefined;
  estimatorDetails: IEstimatorDetails;
  selectedSystemOutput: IViewSolarEstimationOutputParams;
  setSelectedSystemOutput: React.Dispatch<
    React.SetStateAction<IViewSolarEstimationOutputParams>
  >;
  setIsResetRecommendations: React.Dispatch<React.SetStateAction<boolean>>;
  configurations?: Configurations;
  sendConfigurations?: (configurations: Configurations) => void;
  isReset: boolean;
  setIsReset: React.Dispatch<React.SetStateAction<boolean>>;
  setHasChanged: React.Dispatch<React.SetStateAction<boolean>>;
  setDischargeRate: React.Dispatch<React.SetStateAction<number>>;
  setWaterDemand: React.Dispatch<React.SetStateAction<number>>;
  estimationNo: number;
  estimationType: ESolarSolutionType;
  controllerRequired: boolean;
  setControllerRequired: React.Dispatch<React.SetStateAction<boolean>>;
  systemType?: ESolarSystemType;
  isDisabledControllerSwitch: boolean;
}

const AssembleSystem = (props: IProps) => {
  const {
    assembleSystemData,
    estimatorDetails,
    selectedSystemOutput,
    isReset,
    setSelectedSystemOutput,
    setIsResetRecommendations,
    configurations,
    sendConfigurations,
    setIsReset,
    setHasChanged,
    estimationNo,
    setDischargeRate,
    setWaterDemand,
    estimationType,
    controllerRequired,
    setControllerRequired,
    systemType,
    isDisabledControllerSwitch,
  } = props;

  const auth = useSelector(selectAuth);

  const [searchValue, setSearchValue] = useState("");
  const [selectedSolarComponent, setSelectedSolarComponent] =
    useState<ISelectedSolarComponent>({
      id: 1,
      category: EViewSolarEstimationSolarProductCategory.INVERTERS,
    });
  const [isShowPreferencesModal, setIsShowPreferencesModal] = useState(false);
  const [batteries, setBatteries] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [panels, setPanels] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [inverters, setInverters] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [controllers, setControllers] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [waterHeaters, setWaterHeaters] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [waterHeaterControllers, setWaterHeaterControllers] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [recommendations, setRecommendations] = useState<
    IViewSolarRecommendations[]
  >([]);
  const [selectedComponent, setSelectedComponent] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedInverter, setSelectedInverter] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedBattery, setSelectedBattery] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedPanel, setSelectedPanel] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedController, setSelectedController] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedWaterHeaterController, setSelectedWaterHeaterController] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [selectedWaterHeater, setSelectedWaterHeater] =
    useState<IViewSolarEstimationSolarProductAndOtherDetails>();
  const [userSelected, setUserSelected] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [originalAssembleSystemData, setOriginalAssembleSystemData] =
    useState<IViewSolarEstimationData>();
  const [currency, setCurrency] = useState("");
  const [isShowUpdateHistoryModal, setIsShowUpdateHistoryModal] =
    useState(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [comment, setComment] = useState("");

  const [triggerUserSelection, { isLoading: isLoadingUserSelection }] =
    useUserSelectionMutation();
  const [
    triggerLazyViewSolarEstimation,
    { isLoading: isLoadingViewEstimation },
  ] = useLazyViewSolarEstimationQuery();

  useEffect(() => {
    setOriginalAssembleSystemData(assembleSystemData);

    if (!assembleSystemData) return;

    const inverters = assembleSystemData.recommendedProducts?.INVERTERS ?? [];
    const batteries = assembleSystemData.recommendedProducts?.BATTERIES ?? [];
    const panels = assembleSystemData.recommendedProducts?.PANELS ?? [];
    const controllers =
      assembleSystemData.recommendedProducts?.CHARGE_CONTROLLERS ?? [];
    const waterHeaterControllers =
      assembleSystemData.recommendedProducts?.WATER_HEATER_CONTROLLERS ?? [];
    const waterHeaters =
      assembleSystemData.recommendedProducts?.WATER_HEATERS ?? [];

    setInverters(inverters);
    setBatteries(batteries);
    setPanels(panels);
    setControllers(controllers);
    setWaterHeaterControllers(waterHeaterControllers);
    setWaterHeaters(waterHeaters);
    setRecommendations(
      sortRecommendations(assembleSystemData.recommendations || []),
    );

    const idsOfRecommended =
      (assembleSystemData.recommendations || []).map((r) => r.id) || [];

    const userSelectedProducts = assembleSystemData.adminSelectedProducts
      ? assembleSystemData.adminSelectedProducts
      : assembleSystemData.userSelectedProducts;
    if (userSelectedProducts && userSelectedProducts.length > 0) {
      const idsOfUserSelectedProducts = userSelectedProducts.map(
        (usp) => usp.id,
      );

      userSelectedProducts.forEach((usp) => {
        const recIsSelected =
          (assembleSystemData.userSelectedProducts
            ? assembleSystemData.userSelectedProducts
            : assembleSystemData.recommendations
          )?.find((r) => r.id === usp.id)?.isSelected ?? usp.isSelected;

        switch (usp.category) {
          case EViewSolarEstimationSolarProductCategory.INVERTERS:
            if (!selectedInverter) {
              const inverter = inverters.find(
                (inv: IViewSolarEstimationSolarProductAndOtherDetails) =>
                  idsOfUserSelectedProducts.includes(inv.product.id),
              );
              setSelectedInverter(
                inverter
                  ? { ...inverter, count: usp.count, isSelected: recIsSelected }
                  : undefined,
              );
            }
            break;
          case EViewSolarEstimationSolarProductCategory.BATTERIES:
            if (!selectedBattery) {
              const battery = batteries.find(
                (bat: IViewSolarEstimationSolarProductAndOtherDetails) =>
                  idsOfUserSelectedProducts.includes(bat.product.id),
              );
              setSelectedBattery(
                battery
                  ? { ...battery, count: usp.count, isSelected: recIsSelected }
                  : undefined,
              );
            }
            break;
          case EViewSolarEstimationSolarProductCategory.PANELS:
            if (!selectedPanel) {
              const panel = panels.find(
                (pan: IViewSolarEstimationSolarProductAndOtherDetails) =>
                  idsOfUserSelectedProducts.includes(pan.product.id),
              );
              setSelectedPanel(
                panel
                  ? { ...panel, count: usp.count, isSelected: recIsSelected }
                  : undefined,
              );
            }
            break;
          case EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS:
            if (!selectedController) {
              const controller = controllers.find(
                (con: IViewSolarEstimationSolarProductAndOtherDetails) =>
                  idsOfUserSelectedProducts.includes(con.product.id),
              );
              setSelectedController(
                controller
                  ? {
                      ...controller,
                      count: usp.count,
                      isSelected: recIsSelected,
                    }
                  : undefined,
              );
            }
            break;
          case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
            if (!selectedWaterHeaterController) {
              const controller = waterHeaterControllers.find(
                (con: IViewSolarEstimationSolarProductAndOtherDetails) =>
                  idsOfUserSelectedProducts.includes(con.product.id),
              );
              setSelectedWaterHeaterController(
                controller
                  ? {
                      ...controller,
                      count: usp.count,
                      isSelected: recIsSelected,
                    }
                  : undefined,
              );
            }
            break;
          case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
            if (!selectedWaterHeater) {
              const wh = waterHeaters.find(
                (w: IViewSolarEstimationSolarProductAndOtherDetails) =>
                  idsOfUserSelectedProducts.includes(w.product.id),
              );
              setSelectedWaterHeater(
                wh
                  ? { ...wh, count: usp.count, isSelected: recIsSelected }
                  : undefined,
              );
            }
            break;
        }
      });

      return;
    }

    if (!selectedInverter) {
      const inverter = inverters.find(
        (inv: IViewSolarEstimationSolarProductAndOtherDetails) =>
          idsOfRecommended.includes(inv.product.id),
      );
      const recInverter = assembleSystemData.recommendations?.find(
        (r) => r.id === inverter?.product.id,
      );
      setSelectedInverter(
        inverter
          ? { ...inverter, isSelected: recInverter?.isSelected ?? true }
          : undefined,
      );
    }

    if (!selectedBattery) {
      const battery = batteries.find(
        (bat: IViewSolarEstimationSolarProductAndOtherDetails) =>
          idsOfRecommended.includes(bat.product.id),
      );
      const recBattery = assembleSystemData.recommendations?.find(
        (r) => r.id === battery?.product.id,
      );
      setSelectedBattery(
        battery
          ? { ...battery, isSelected: recBattery?.isSelected ?? true }
          : undefined,
      );
    }

    if (!selectedPanel) {
      const panel = panels.find(
        (pan: IViewSolarEstimationSolarProductAndOtherDetails) =>
          idsOfRecommended.includes(pan.product.id),
      );
      const recPanel = assembleSystemData.recommendations?.find(
        (r) => r.id === panel?.product.id,
      );
      setSelectedPanel(
        panel
          ? { ...panel, isSelected: recPanel?.isSelected ?? true }
          : undefined,
      );
    }

    if (!selectedController) {
      const controller = controllers.find(
        (con: IViewSolarEstimationSolarProductAndOtherDetails) =>
          idsOfRecommended.includes(con.product.id),
      );
      const recController = assembleSystemData.recommendations?.find(
        (r) => r.id === controller?.product.id,
      );
      setSelectedController(
        controller
          ? { ...controller, isSelected: recController?.isSelected ?? true }
          : undefined,
      );
    }

    if (!selectedWaterHeater) {
      const wh = waterHeaters.find(
        (wh: IViewSolarEstimationSolarProductAndOtherDetails) =>
          idsOfRecommended.includes(wh.product.id),
      );
      const recWh = assembleSystemData.recommendations?.find(
        (r) => r.id === wh?.product.id,
      );
      setSelectedWaterHeater(
        wh ? { ...wh, isSelected: recWh?.isSelected ?? true } : undefined,
      );
    }

    if (!selectedWaterHeaterController) {
      const whController = waterHeaterControllers.find(
        (con: IViewSolarEstimationSolarProductAndOtherDetails) =>
          idsOfRecommended.includes(con.product.id),
      );
      const recWhController = assembleSystemData.recommendations?.find(
        (r) => r.id === whController?.product.id,
      );
      setSelectedWaterHeaterController(
        whController
          ? {
              ...whController,
              isSelected: recWhController?.isSelected ?? true,
            }
          : undefined,
      );
    }
  }, [assembleSystemData]);

  useEffect(() => {
    if (!controllerRequired) {
      setSelectedWaterHeaterController(undefined);
    }

    if (estimationType === ESolarSolutionType.WATER_HEATING_SYSTEM) {
      setSelectedInverter(undefined);
      setSelectedBattery(undefined);
      setSelectedPanel(undefined);

      setSelectedSolarComponent({
        id: 1,
        category: EViewSolarEstimationSolarProductCategory.WATER_HEATERS,
      });
    } else {
      if (estimationType === ESolarSolutionType.SOLAR_ENERGY_SYSTEM) {
        setSelectedWaterHeater(undefined);
        setSelectedWaterHeaterController(undefined);
      }

      setSelectedSolarComponent({
        id: 1,
        category: EViewSolarEstimationSolarProductCategory.INVERTERS,
      });
    }
  }, [controllerRequired, estimationType, systemType]);

  useEffect(() => {
    const getSelectedComponentByCategory = () => {
      switch (selectedSolarComponent.category) {
        case EViewSolarEstimationSolarProductCategory.BATTERIES:
          return selectedBattery;
        case EViewSolarEstimationSolarProductCategory.PANELS:
          return selectedPanel;
        case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
          return selectedWaterHeater;
        case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
          return selectedWaterHeaterController;
        case EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS:
          return selectedController;
        default:
          return selectedInverter;
      }
    };

    setSelectedComponent(getSelectedComponentByCategory());
  }, [
    selectedSolarComponent.category,
    selectedInverter,
    selectedBattery,
    selectedPanel,
    selectedController,
    selectedWaterHeaterController,
    selectedWaterHeater,
  ]);

  useEffect(() => {
    const userSelections: IViewSolarEstimationSolarProductAndOtherDetails[] =
      [];

    if (selectedInverter) {
      userSelections.push({
        ...selectedInverter,
      });
    }

    if (selectedBattery) {
      userSelections.push({
        ...selectedBattery,
      });
    }

    if (selectedPanel) {
      userSelections.push({ ...selectedPanel });
    }

    if (selectedController) {
      userSelections.push({
        ...selectedController,
      });
    }

    if (selectedWaterHeaterController) {
      userSelections.push({
        ...selectedWaterHeaterController,
      });
    }

    if (selectedWaterHeater) {
      userSelections.push({
        ...selectedWaterHeater,
      });
    }

    setUserSelected([...userSelections]);
  }, [
    selectedInverter?.product.id,
    selectedInverter?.count,
    selectedInverter?.isSelected,
    selectedBattery?.product.id,
    selectedBattery?.count,
    selectedBattery?.isSelected,
    selectedPanel?.product.id,
    selectedPanel?.count,
    selectedPanel?.isSelected,
    selectedController?.product.id,
    selectedController?.count,
    selectedController?.isSelected,
    selectedWaterHeaterController?.product.id,
    selectedWaterHeaterController?.count,
    selectedWaterHeaterController?.isSelected,
    selectedWaterHeater?.product.id,
    selectedWaterHeater?.count,
    selectedWaterHeater?.isSelected,
  ]);

  useEffect(() => {
    const userSelections: ISolarEstimationUserSelection[] = userSelected.map(
      (us) => ({
        productCategory: us.product.category,
        productId: us.product.id,
        count: us.count,
        isSelected: us.isSelected,
      }),
    );

    if (
      estimatorDetails.estimationId &&
      estimatorDetails.estimationToken &&
      userSelections.length > 0
    ) {
      triggerUserSelection({
        estimationId: auth.token ? estimatorDetails.estimationId : "",
        estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
        userSelections,
      })
        .unwrap()
        .then((userSelectionRes) => {
          setValidationMessages(userSelectionRes.validationMessages);
          triggerLazyViewSolarEstimation(
            auth.token
              ? {
                  estimationId: estimatorDetails.estimationId,
                }
              : {
                  estimatorToken: estimatorDetails.estimationToken,
                },
          )
            .unwrap()
            .then((res) => {
              setComment(res.estimationView.adminComment || "");
              setSelectedSystemOutput(
                res.estimationView.data.outputParams || {
                  totalWattHours: 0,
                  totalCost: 0,
                  systemVoltage: 0,
                  totalAmpHours: 0,
                  nightLoadAmpHours: 0,
                  batteryAmpHours: 0,
                  maxPowerInWatt: 0,
                  grandTotal: 0,
                  totalAccessoriesCost: 0,
                  laborCost: 0,
                  totalLiters: 0,
                },
              );
              setCurrency(res.estimationView.currency);
              setDischargeRate(
                res.estimationView.data.inputParams.dischargeRatePercentage ||
                  0,
              );
              setWaterDemand(
                res.estimationView.data.inputParams.waterDemand || 0,
              );
            })
            .catch(() => {
              setSelectedSystemOutput({
                totalWattHours: 0,
                totalCost: 0,
                systemVoltage: 0,
                totalAmpHours: 0,
                nightLoadAmpHours: 0,
                batteryAmpHours: 0,
                maxPowerInWatt: 0,
                grandTotal: 0,
                totalAccessoriesCost: 0,
                laborCost: 0,
                totalLiters: 0,
              });

              setCurrency("");
            });
        })
        .catch(() => {
          setValidationMessages([]);
          setSelectedSystemOutput({
            totalWattHours: 0,
            totalCost: 0,
            systemVoltage: 0,
            totalAmpHours: 0,
            nightLoadAmpHours: 0,
            batteryAmpHours: 0,
            maxPowerInWatt: 0,
            grandTotal: 0,
            totalAccessoriesCost: 0,
            laborCost: 0,
            totalLiters: 0,
          });

          setCurrency("");
        });
    }
  }, [
    userSelected,
    estimatorDetails.estimationId,
    estimatorDetails.estimationToken,
  ]);

  useEffect(() => {
    if (isReset) {
      onReset(false);
      setIsReset(false);
    }
  }, [isReset]);

  const onCountChange = (
    id: string,
    count: number,
    category: EViewSolarEstimationSolarProductCategory,
  ) => {
    switch (category) {
      case EViewSolarEstimationSolarProductCategory.INVERTERS:
        setSelectedInverter((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setInverters((ps) => {
          return ps.map((inverter) => {
            return {
              ...inverter,
              count: inverter.product.id === id ? count : inverter.count,
            };
          });
        });
        break;
      case EViewSolarEstimationSolarProductCategory.BATTERIES:
        setSelectedBattery((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setBatteries((ps) => {
          return ps.map((battery) => {
            return {
              ...battery,
              count: battery.product.id === id ? count : battery.count,
            };
          });
        });
        break;
      case EViewSolarEstimationSolarProductCategory.PANELS:
        setSelectedPanel((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setPanels((ps) => {
          return ps.map((panel) => {
            return {
              ...panel,
              count: panel.product.id === id ? count : panel.count,
            };
          });
        });
        break;
      case EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS:
        setSelectedController((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setControllers((ps) => {
          return ps.map((controller) => {
            return {
              ...controller,
              count: controller.product.id === id ? count : controller.count,
            };
          });
        });
        break;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
        setSelectedWaterHeaterController((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setWaterHeaterControllers((ps) => {
          return ps.map((controller) => {
            return {
              ...controller,
              count: controller.product.id === id ? count : controller.count,
            };
          });
        });
        break;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
        setSelectedWaterHeater((ps) => {
          if (ps) {
            return {
              ...ps,
              count,
            };
          }
        });
        setWaterHeaters((ps) => {
          return ps.map((waterHeater) => {
            return {
              ...waterHeater,
              count: waterHeater.product.id === id ? count : waterHeater.count,
            };
          });
        });
        break;
    }
  };

  const onSelectComponent = (
    category: EViewSolarEstimationSolarProductCategory,
    id: string,
  ) => {
    switch (category) {
      case EViewSolarEstimationSolarProductCategory.INVERTERS: {
        const inverter = inverters.find(
          (inverter) => inverter.product.id === id,
        );
        setSelectedInverter(
          inverter ? { ...inverter, isSelected: true } : undefined,
        );
        break;
      }
      case EViewSolarEstimationSolarProductCategory.BATTERIES: {
        const battery = batteries.find((battery) => battery.product.id === id);
        setSelectedBattery(
          battery ? { ...battery, isSelected: true } : undefined,
        );
        break;
      }
      case EViewSolarEstimationSolarProductCategory.PANELS: {
        const panel = panels.find((panel) => panel.product.id === id);
        setSelectedPanel(panel ? { ...panel, isSelected: true } : undefined);
        break;
      }
      case EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS:
        setSelectedController((ps) => {
          if (ps?.product.id === id) {
            return {
              ...ps,
              isSelected: !ps.isSelected,
            };
          }
          const controller = controllers.find((c) => c.product.id === id);
          return controller ? { ...controller, isSelected: true } : undefined;
        });
        break;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS: {
        const waterHeater = waterHeaters.find(
          (waterHeater) => waterHeater.product.id === id,
        );
        setSelectedWaterHeater(
          waterHeater ? { ...waterHeater, isSelected: true } : undefined,
        );
        break;
      }
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS: {
        const waterHeaterController = waterHeaterControllers.find(
          (controller) => controller.product.id === id,
        );
        setSelectedWaterHeaterController(
          waterHeaterController
            ? { ...waterHeaterController, isSelected: true }
            : undefined,
        );
        break;
      }
    }
  };

  const onReset = (
    isShowMessage: boolean,
    category?: EViewSolarEstimationSolarProductCategory,
  ) => {
    if (!category) {
      triggerUserSelection({
        estimationId: auth.token ? estimatorDetails.estimationId : "",
        estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
        userSelections: [],
      })
        .unwrap()
        .then(() => {
          setSelectedInverter(undefined);
          setSelectedBattery(undefined);
          setSelectedPanel(undefined);
          setSelectedController(undefined);
          setSelectedWaterHeaterController(undefined);
          setSelectedWaterHeater(undefined);

          setIsResetRecommendations((ps) => !ps);

          if (isShowMessage) {
            showSuccessMessage("Reset successful");
          }
        })
        .catch(() => {
          if (isShowMessage) {
            showErrorMessage("Failed to reset");
          }
        });

      return;
    }

    if (originalAssembleSystemData) {
      const idsOfRecommended =
        originalAssembleSystemData.recommendations?.map(
          (recommended) => recommended.id,
        ) || [];

      switch (category) {
        case EViewSolarEstimationSolarProductCategory.INVERTERS:
          setSelectedInverter(undefined);
          setInverters([
            ...(originalAssembleSystemData.recommendedProducts?.INVERTERS ||
              []),
          ]);
          setSelectedInverter(
            originalAssembleSystemData.recommendedProducts?.INVERTERS.find(
              (inverter) => idsOfRecommended.includes(inverter.product.id),
            ),
          );
          break;
        case EViewSolarEstimationSolarProductCategory.BATTERIES:
          setSelectedBattery(undefined);
          setBatteries([
            ...(originalAssembleSystemData.recommendedProducts?.BATTERIES ||
              []),
          ]);
          setSelectedBattery(
            originalAssembleSystemData.recommendedProducts?.BATTERIES.find(
              (battery) => idsOfRecommended.includes(battery.product.id),
            ),
          );
          break;
        case EViewSolarEstimationSolarProductCategory.PANELS:
          setSelectedPanel(undefined);
          setPanels([
            ...(originalAssembleSystemData.recommendedProducts?.PANELS || []),
          ]);
          setSelectedPanel(
            originalAssembleSystemData.recommendedProducts?.PANELS.find(
              (panel) => idsOfRecommended.includes(panel.product.id),
            ),
          );
          break;
        case EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS:
          setSelectedController(undefined);
          setControllers([
            ...(originalAssembleSystemData.recommendedProducts
              ?.CHARGE_CONTROLLERS || []),
          ]);
          setSelectedController(
            originalAssembleSystemData.recommendedProducts?.CHARGE_CONTROLLERS.find(
              (controller) => idsOfRecommended.includes(controller.product.id),
            ),
          );
          break;
        case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
          setSelectedWaterHeaterController(undefined);
          setWaterHeaterControllers([
            ...(originalAssembleSystemData.recommendedProducts
              ?.WATER_HEATER_CONTROLLERS || []),
          ]);
          setSelectedWaterHeaterController(
            originalAssembleSystemData.recommendedProducts?.WATER_HEATER_CONTROLLERS.find(
              (controller) => idsOfRecommended.includes(controller.product.id),
            ),
          );
          break;
        case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
          setSelectedWaterHeater(undefined);
          setWaterHeaters([
            ...(originalAssembleSystemData.recommendedProducts?.WATER_HEATERS ||
              []),
          ]);
          setSelectedWaterHeater(
            originalAssembleSystemData.recommendedProducts?.WATER_HEATERS.find(
              (waterHeater) =>
                idsOfRecommended.includes(waterHeater.product.id),
            ),
          );
          break;
      }
    }
  };

  const filteredComponents = useMemo(() => {
    const getComponentsByCategory = () => {
      switch (selectedSolarComponent.category) {
        case EViewSolarEstimationSolarProductCategory.BATTERIES:
          return batteries;
        case EViewSolarEstimationSolarProductCategory.PANELS:
          return panels;
        case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
          return waterHeaters;
        case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
          return waterHeaterControllers;
        case EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS:
          return controllers;
        default:
          return inverters;
      }
    };

    let components = getComponentsByCategory();

    components = components.filter((comp) =>
      comp.product.name.toLowerCase().includes(searchValue.toLowerCase()),
    );

    const recommendedIds = new Set(recommendations.map((r) => r.id));
    components = components.sort((a, b) => {
      const aIsRecommended = recommendedIds.has(a.product.id);
      const bIsRecommended = recommendedIds.has(b.product.id);

      if (aIsRecommended && !bIsRecommended) return -1;
      if (!aIsRecommended && bIsRecommended) return 1;
      return 0;
    });

    return components;
  }, [
    selectedSolarComponent.category,
    inverters,
    batteries,
    panels,
    controllers,
    waterHeaterControllers,
    waterHeaters,
    searchValue,
    recommendations,
  ]);

  const getCategoryName = (
    category: EViewSolarEstimationSolarProductCategory,
  ): string => {
    switch (category) {
      case EViewSolarEstimationSolarProductCategory.INVERTERS:
        return "Inverter";
      case EViewSolarEstimationSolarProductCategory.BATTERIES:
        return "Battery";
      case EViewSolarEstimationSolarProductCategory.PANELS:
        return "Panel";
      case EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS:
        return "Charge Controller";
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
        return "Water Heater Controller";
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
        return "Water Heater";
      default:
        return "";
    }
  };

  const solarCategories = [
    EViewSolarEstimationSolarProductCategory.INVERTERS,
    EViewSolarEstimationSolarProductCategory.BATTERIES,
    EViewSolarEstimationSolarProductCategory.PANELS,
    EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS,
  ];

  const waterCategories = [
    EViewSolarEstimationSolarProductCategory.WATER_HEATERS,
    EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS,
  ];

  const solarItems = filterByCategories(userSelected, solarCategories);

  const waterItems = filterByCategories(userSelected, waterCategories);

  return (
    <>
      <div className="px-4">
        <Row className="align-items-start gy-4">
          <Col xl={4}>
            <Row className="align-items-center justify-content-between">
              <Col>
                <Row>
                  <Col className="text-advance-description font-size-14 font-weight-500">
                    Components Cost
                  </Col>
                </Row>
                <Row>
                  <Col className="text-label font-size-22 font-weight-600">
                    {formatWithCommas(
                      Number(Helper.roundTo2(selectedSystemOutput.totalCost)),
                    )}
                    &nbsp;{currency}
                  </Col>
                </Row>
              </Col>
              <Col className="col-auto">
                <Row className="align-items-center gap-3">
                  <Col className="col-auto pe-0">
                    <div
                      className={`${styles.updateHistoryIconWrapper} bg-background-6 rounded-2 p-2 cursor-pointer`}
                      onClick={() => setIsShowUpdateHistoryModal(true)}
                    >
                      <MaterialIcon
                        icon="receipt_long"
                        color="var(--color-receipt-icon)"
                        className="bg-background-5 d-flex justify-content-center align-items-center rounded-circle "
                        size={16}
                        style={{ width: "23px", height: "23px" }}
                      />
                      {comment && (
                        <div className={styles.updateHistoryCount}>1</div>
                      )}
                    </div>
                  </Col>
                  <Col
                    className="col-auto cursor-pointer bg-background-6 rounded-2 p-2"
                    onClick={() => setIsShowPreferencesModal(true)}
                  >
                    <Row className="align-items-center">
                      <Col className="col-auto pe-2">
                        <MaterialIcon
                          icon="settings"
                          color="var(--color-receipt-icon)"
                          className="bg-background-5 d-flex justify-content-center align-items-center rounded-circle"
                          size={16}
                          style={{ width: "23px", height: "23px" }}
                        />
                      </Col>
                      <Col className="title-4 font-size-14 font-weight-500 ps-0">
                        Preferences
                      </Col>
                    </Row>
                  </Col>
                  <Col
                    className="cursor-pointer col-auto bg-background-6 rounded-2 p-2"
                    onClick={() => {
                      onReset(true);
                    }}
                  >
                    <MaterialIcon
                      icon="undo"
                      color="var(--color-solar-system-type-title)"
                      size={16}
                      className="d-flex justify-content-center align-items-center"
                      style={{ width: "23px", height: "23px" }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col className={`${styles.componentsWrapper}`}>
                {(estimationType === ESolarSolutionType.SOLAR_ENERGY_SYSTEM ||
                  estimationType === ESolarSolutionType.BOTH) &&
                  solarItems.length > 0 && (
                    <>
                      <Row className="mb-2">
                        <Col className="text-secondary font-weight-500 font-size-14">
                          Solar Power Equipments
                        </Col>
                      </Row>
                      {solarItems.map((us, index) => {
                        const product = us.product;
                        const quantity = us.count;
                        const id = index + 1;

                        return (
                          <Row
                            key={`${product.id}-${product.category}`}
                            className={index === 0 ? "" : "mt-2"}
                          >
                            <Col>
                              <SolarComponent
                                component={{
                                  id,
                                  category: product.category,
                                  name: product.name,
                                  quantity,
                                  value: product.price,
                                  unit: product.currency,
                                }}
                                selected={selectedSolarComponent}
                                onSelect={(
                                  id: number,
                                  category: EViewSolarEstimationSolarProductCategory,
                                ) => {
                                  setSelectedSolarComponent({
                                    id,
                                    category,
                                  });
                                }}
                                isWarning={validationMessages.some((msg) => {
                                  const category = product.category;
                                  const getCategoryName = () =>
                                    category ===
                                    EViewSolarEstimationSolarProductCategory.INVERTERS
                                      ? "inverter"
                                      : category ===
                                          EViewSolarEstimationSolarProductCategory.BATTERIES
                                        ? "battery"
                                        : category ===
                                            EViewSolarEstimationSolarProductCategory.PANELS
                                          ? "panel"
                                          : "controller";
                                  return msg.includes(getCategoryName());
                                })}
                              />
                            </Col>
                          </Row>
                        );
                      })}
                    </>
                  )}
                {(estimationType === ESolarSolutionType.BOTH ||
                  estimationType === ESolarSolutionType.WATER_HEATING_SYSTEM) &&
                  waterItems.length > 0 && (
                    <>
                      <Row className="mt-4 mb-2">
                        <Col className="text-secondary font-weight-500 font-size-14">
                          Water Heater Equipments
                        </Col>
                      </Row>
                      {waterItems.map((us, index) => {
                        const product = us.product;
                        const quantity = us.count;
                        const id = solarItems.length + index + 1;

                        return (
                          <Row
                            key={`${product.id}-${product.category}`}
                            className={index === 0 ? "" : "mt-2"}
                          >
                            <Col>
                              <SolarComponent
                                component={{
                                  id,
                                  category: product.category,
                                  name: product.name,
                                  quantity,
                                  value: product.price,
                                  unit: product.currency,
                                }}
                                selected={selectedSolarComponent}
                                onSelect={(
                                  id: number,
                                  category: EViewSolarEstimationSolarProductCategory,
                                ) => {
                                  setSelectedSolarComponent({
                                    id,
                                    category,
                                  });
                                }}
                                isWarning={validationMessages.some((msg) => {
                                  const category = product.category;
                                  const getCategoryName = () =>
                                    category ===
                                    EViewSolarEstimationSolarProductCategory.WATER_HEATERS
                                      ? "water heater"
                                      : "water heater controller";
                                  return msg.includes(getCategoryName());
                                })}
                              />
                            </Col>
                          </Row>
                        );
                      })}
                      <Row className="align-items-center mt-4">
                        <Col className="col-auto">
                          <AppSwitch
                            isOn={controllerRequired}
                            onSwitch={(isOn) => setControllerRequired(isOn)}
                            size="sm"
                            disabled={
                              isDisabledControllerSwitch ||
                              isLoadingUserSelection ||
                              isLoadingViewEstimation
                            }
                          />
                        </Col>
                        <Col>
                          <Row>
                            <Col className="title-1 font-weight-500">
                              Add Controller (Optional)
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <Col className="text-advance-description font-weight-400 font-size-12">
                                Controller unit is optional but recommended. It
                                regulates temperature difference, prevents
                                overheating and heat loss, and improves system
                                efficiency.
                              </Col>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </>
                  )}
              </Col>
            </Row>
          </Col>
          <Col>
            <Row className="align-items-center">
              <Col className="text-label font-size-20 font-weight-600">
                Select&nbsp;{getCategoryName(selectedSolarComponent.category)}
              </Col>
              <Col xl={4}>
                <Search
                  placeholder={`Search ${getCategoryName(
                    selectedSolarComponent.category,
                  )}`}
                  handleSearchChange={(searchText: string) => {
                    setSearchValue(searchText);
                  }}
                  onSearch={() => {
                    console.log();
                  }}
                  value={searchValue}
                  isHideSearchButton
                />
              </Col>
            </Row>
            <Row className="mt-4">
              <Col className={`${styles.componentsWrapper}`}>
                {selectedComponent &&
                  (selectedComponent.product.category !==
                    EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS ||
                    selectedComponent.isSelected) && (
                    <Row>
                      <Col>
                        <RecommendedSolarComponentOption
                          id={selectedComponent.product.id}
                          name={selectedComponent.product.name}
                          category={selectedComponent.product.category}
                          maxOperatingVoltage={
                            selectedComponent.product.maxOperatingVoltage
                          }
                          price={selectedComponent.product.price}
                          currency={selectedComponent.product.currency}
                          quantity={selectedComponent.count}
                          liters={selectedComponent.product.liters}
                          ratedPower={selectedComponent.product.ratedPower}
                          amperage={selectedComponent.product.amperage}
                          batteryVoltage={
                            selectedComponent.product.batteryVoltage
                          }
                          isRecommended={recommendations
                            .map((r) => r.id)
                            .includes(selectedComponent.product.id)}
                          onCountChange={onCountChange}
                          validationMessage={validationMessages.find((msg) => {
                            const category = selectedComponent.product.category;

                            if (
                              category ===
                              EViewSolarEstimationSolarProductCategory.INVERTERS
                            ) {
                              return msg.includes("inverter");
                            }

                            if (
                              category ===
                              EViewSolarEstimationSolarProductCategory.BATTERIES
                            ) {
                              return msg.includes("battery");
                            }

                            if (
                              category ===
                              EViewSolarEstimationSolarProductCategory.PANELS
                            ) {
                              return msg.includes("panel");
                            }

                            if (
                              category ===
                              EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS
                            ) {
                              return msg.includes("controller");
                            }

                            if (
                              category ===
                              EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS
                            ) {
                              return msg.includes("water heater controller");
                            }

                            if (
                              category ===
                              EViewSolarEstimationSolarProductCategory.WATER_HEATERS
                            ) {
                              return msg.includes("water heater");
                            }
                          })}
                        />
                      </Col>
                    </Row>
                  )}
                <Row className="align-items-center mt-4">
                  <Col className="text-advance-description font-size-14 font-weight-500">
                    All Options
                  </Col>
                  <Col
                    className="col-auto cursor-pointer"
                    onClick={() => {
                      onReset(false, selectedSolarComponent.category);
                    }}
                  >
                    <MaterialIcon
                      icon="undo"
                      color="#1A3C84"
                      size={16}
                      className="bg-background-9 p-2 rounded-3"
                    />
                  </Col>
                </Row>
                <Row>
                  {filteredComponents.length > 0 ? (
                    filteredComponents.map((comp) => {
                      const product = comp.product;

                      return (
                        <Col key={product.id} className="col-auto mt-4">
                          <SolarComponentOption
                            selected={
                              product.category ===
                              EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS
                                ? selectedComponent?.product.id ===
                                    product.id && selectedComponent?.isSelected
                                  ? product.id
                                  : ""
                                : selectedComponent?.product.id || ""
                            }
                            id={product.id}
                            isRecommended={recommendations
                              .map((r) => r.id)
                              .includes(product.id)}
                            category={product.category}
                            name={product.name}
                            maxOperatingVoltage={product.maxOperatingVoltage}
                            unit={product.currency}
                            price={product.price}
                            liters={product.liters}
                            ratedPower={product.ratedPower}
                            amperage={product.amperage}
                            batteryVoltage={product.batteryVoltage}
                            onSelect={onSelectComponent}
                          />
                        </Col>
                      );
                    })
                  ) : (
                    <Col className="text-center text-light font-weight-400 font-size-14 mt-2">
                      <div className="container-dash ">
                        No available options
                      </div>
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <PreferencesModal
        show={isShowPreferencesModal}
        onCancel={() => {
          setIsShowPreferencesModal(false);
        }}
        onClose={() => {
          setIsShowPreferencesModal(false);
        }}
        onConfirm={() => {
          onReset(false);
        }}
        configurations={configurations}
        sendConfigurations={sendConfigurations}
        setHasChanged={setHasChanged}
      />
      <UpdateHistoryModal
        show={isShowUpdateHistoryModal}
        onClose={() => setIsShowUpdateHistoryModal(false)}
        estimationId={estimatorDetails.estimationId}
        estimatorToken={estimatorDetails.estimationToken}
        currency={currency}
        estimationNo={estimationNo}
      />
      <SpinnerModal show={isLoadingUserSelection || isLoadingViewEstimation} />
    </>
  );
};

export default AssembleSystem;
